import { t } from "@lingui/macro";

import type { UpgradeTier } from "@/client/hooks/use-upgrade-checkout";
import type { PaywallSignal } from "@/client/stores/paywall";

export type PaywallCopy = {
  title: string;
  description: string;
  /** Which plan the dialog offers. */
  tier: UpgradeTier;
  cta: string;
};

/**
 * Which plan actually unlocks the blocked feature.
 *
 * `advancedAnalytics` and `byokOpenAi` are Ultimate-only; everything else is
 * unlocked by Pro, except for a user who is already on Pro — offering them Pro
 * again is the one thing a paywall must never do.
 */
const tierFor = (signal: PaywallSignal): UpgradeTier => {
  if (signal.feature === "advancedAnalytics" || signal.feature === "byokOpenAi") return "ULTIMATE";
  return signal.currentTier === "PRO" ? "ULTIMATE" : "PRO";
};

/**
 * Localised copy for a server paywall code.
 *
 * A function, not a module-level const map: `lingui/t-call-in-function` forbids
 * a `t` call at module scope, and the catalogs must be read at render time so
 * a locale switch re-renders the right language.
 */
export const translatePaywall = (signal: PaywallSignal): PaywallCopy => {
  const tier = tierFor(signal);
  const cta = tier === "ULTIMATE" ? t`Upgrade to Ultimate` : t`Upgrade to Pro`;

  switch (signal.code) {
    case "APPLICATIONS_DAILY_LIMIT_REACHED": {
      return {
        tier,
        cta,
        title: t`Daily application limit reached`,
        description: t`You have used every application your plan allows today. The counter resets tomorrow — or upgrade now for a higher daily limit.`,
      };
    }
    case "APPLICATIONS_MONTHLY_LIMIT_REACHED": {
      return {
        tier,
        cta,
        title: t`Monthly application limit reached`,
        description: t`You have used every application your plan allows this month. Upgrade for a higher monthly limit and keep applying today.`,
      };
    }
    case "APPLICATIONS_NOT_AVAILABLE_ON_TIER": {
      return {
        tier,
        cta,
        title: t`Auto-apply is a paid feature`,
        description: t`Let Apply Edge submit applications for you, tailored to each posting. Upgrade to switch it on.`,
      };
    }
    case "AI_TAILORING_NOT_AVAILABLE_ON_TIER": {
      return {
        tier,
        cta,
        title: t`AI tailoring is a paid feature`,
        description: t`Rewrite your resume for each job in one click, scored against the posting. Upgrade to switch it on.`,
      };
    }
    // Client-triggered, never a server rejection — fired the moment a
    // tailored application lands in REVIEWING for a FREE/expired-trial user
    // (TailoredReviewSection). Plan §5 item 3.2: "move the buying moment to
    // the artifact" instead of a passive trial banner nobody reads.
    case "TAILORED_APPLICATION_READY": {
      return {
        tier,
        cta,
        title: t`Your tailored application is ready`,
        description: t`Upgrade to download it and generate more.`,
      };
    }
    // Client-triggered: a FREE / expired-trial user clicked "Tailor" in the jobs
    // feed and the server refused (limit 0). Same wording as the weekly-email
    // deep link so the buying moment is identical.
    case "TAILORING_UPGRADE_REQUIRED": {
      return {
        tier,
        cta,
        title: t`Tailored CVs are part of Pro`,
        description: t`Get a CV rewritten for each job you pick, scored against the posting. Upgrade to switch it on.`,
      };
    }
    case "JOB_PROFILES_LIMIT_REACHED": {
      return {
        tier,
        cta,
        title: t`Job profile limit reached`,
        description: t`Your plan allows a limited number of saved searches. Upgrade to track more roles, locations, and boards at once.`,
      };
    }
    default: {
      return {
        tier,
        cta,
        title: t`This feature is on a higher plan`,
        description: t`Your current plan does not include this feature. Upgrade to unlock it right away — no re-entering anything, it turns on for your account.`,
      };
    }
  }
};
